import { Box, Text } from "@mantine/core"
import { CaretRightIcon } from "@phosphor-icons/react"
import React, { useMemo, useState } from "react"
import classes from "../analytics.module.css"
import { formatCompactMnt } from "../utils"
import { AnalyticsCard, EmptyRow } from "./AnalyticsCard"

export type HBarItem = {
  id: string
  label: string
  amount: number
  percent: number
  /** Optional secondary label rendered after amount (e.g. " · 30%") */
  valueSuffix?: string
}

type Props = {
  title: string
  subtitle?: string
  items: HBarItem[] | undefined
  isLoading: boolean
  /** When provided, each bar becomes clickable to reveal drill content. */
  renderDrill?: (item: HBarItem) => React.ReactNode
}

// Canvas "Топ 5 бараа" shape: label and value on one line, thin dark bar below.
export function HorizontalBarList({ title, subtitle, items, isLoading, renderDrill }: Props) {
  const [openId, setOpenId] = useState<string | null>(null)

  const maxPercent = useMemo(() => {
    if (!items || items.length === 0) return 100
    return Math.max(...items.map(i => i.percent), 1)
  }, [items])

  return (
    <AnalyticsCard title={title} subtitle={subtitle} isLoading={isLoading}>
      {items && items.length === 0 && !isLoading ? <EmptyRow /> : null}
      {(items ?? []).map(it => {
        const fillWidth = Math.min(100, (it.percent / maxPercent) * 100)
        const { display, unit } = formatCompactMnt(it.amount)
        const isOpen = openId === it.id
        const canDrill = !!renderDrill
        return (
          <Box
            key={it.id}
            className={classes.barItem}
            data-drill={canDrill || undefined}
            onClick={canDrill ? () => setOpenId(prev => prev === it.id ? null : it.id) : undefined}
          >
            <Box className={classes.barTop}>
              <Text className={classes.barLabel} title={it.label}>
                {canDrill ? (
                  <Box component="span" className={classes.caret} data-open={isOpen || undefined}>
                    <CaretRightIcon size={12} weight="bold" />
                  </Box>
                ) : null}
                {it.label}
              </Text>
              <Text className={classes.barValue}>
                {display} {unit}
                <Text component="span" className={classes.barValueMeta}>{it.valueSuffix ?? ` · ${it.percent}%`}</Text>
              </Text>
            </Box>
            <Box className={classes.barTrack}>
              <Box className={classes.barFill} w={`${fillWidth}%`} />
            </Box>
            {canDrill && isOpen && renderDrill ? (
              <Box className={classes.drill} mt="sm" onClick={e => e.stopPropagation()}>{renderDrill(it)}</Box>
            ) : null}
          </Box>
        )
      })}
    </AnalyticsCard>
  )
}
